import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle, RefreshCw, Rocket } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HINTS = {
  queue: "Publish or reject everything in the Pending tab",
  requests: "Resolve open items in the Requests tab",
  sample_data: "Run the held Clean Sample Data step right before sharing the link",
  mailbox: "At your registrar: Zoho Mail free tier, or forward support@proofbuy.in to your Gmail",
  domain: "Emergent dashboard → Link domain → proofbuy.in → Entri (remove old A records first). After it's green, ask the builder to flip the app's public URL",
  email: "Managed by the platform — no action needed",
  alerts: "Tell the builder which inbox should get alerts (your Gmail works) — non-blocking",
  waitlist: "",
};

export const LaunchPanel = ({ token }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/launch-readiness`, { headers: { Authorization: `Bearer ${token}` } });
      setData(data);
    } catch {
      setData({ checks: [], ready: false });
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (data === null) {
    return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-trust" /></div>;
  }

  const remaining = data.checks.filter((c) => !c.ok).length;

  return (
    <div data-testid="launch-panel">
      <div
        data-testid="launch-status-banner"
        className={`rounded-2xl p-7 flex items-center gap-4 ${data.ready ? "bg-verified-soft border border-verified/25" : "bg-gold-soft border border-gold/40"}`}
      >
        <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${data.ready ? "bg-verified/15 text-verified" : "bg-gold/25 text-yellow-700"}`}>
          <Rocket size={20} />
        </span>
        <div>
          <p className="font-display font-semibold text-lg">
            {data.ready ? "Ready to share the link." : `Not yet — ${remaining} thing${remaining > 1 ? "s" : ""} left.`}
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            {data.ready ? "Every launch check passes." : "Work through the list below, then re-run."}
          </p>
        </div>
        <button data-testid="launch-refresh-button" onClick={load}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 px-4 py-2 text-xs font-semibold text-gray-600 hover:border-trust hover:text-trust transition-colors duration-200">
          <RefreshCw size={13} /> Re-check
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {data.checks.map((c) => (
          <div key={c.id} data-testid={`launch-check-${c.id}`}
            className="rounded-2xl bg-white border border-black/5 shadow-sm p-5 flex items-start gap-4">
            {c.ok ? (
              <CheckCircle2 size={20} className="text-verified shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle size={20} className="text-warn shrink-0 mt-0.5" />
            )}
            <div>
              <p className="font-semibold text-sm">{c.label}</p>
              <p className="mt-0.5 text-xs text-gray-500">{c.detail}</p>
              {!c.ok && HINTS[c.id] && <p className="mt-1.5 text-xs font-semibold text-trust">{HINTS[c.id]}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
